import { supabase } from "@/integrations/supabase/client";

export interface VerificationStatus {
  isVerified: boolean;
  canSignIn: boolean;
  userExists: boolean;
  email?: string;
  lastSignInAt?: string;
  message: string;
  actionRequired: 'none' | 'resend_email' | 'signin' | 'contact_support';
  severity: 'success' | 'info' | 'warning' | 'error';
}

export interface TokenValidationResult {
  isValid: boolean;
  isExpired: boolean;
  isAlreadyUsed: boolean;
  tokenType?: string;
  email?: string;
  message: string;
  suggestedAction: 'retry' | 'resend' | 'signin' | 'contact_support';
}

/**
 * Check user verification status independently of token validation
 */
export const checkUserVerificationStatus = async (email?: string): Promise<VerificationStatus> => {
  try {
    // First check if there's a current session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session?.user) {
      const isVerified = !!session.user.email_confirmed_at;
      return {
        isVerified,
        canSignIn: true,
        userExists: true,
        email: session.user.email,
        lastSignInAt: session.user.last_sign_in_at,
        message: isVerified 
          ? "Your email is verified and you're signed in successfully!"
          : "You're signed in but your email isn't verified yet.",
        actionRequired: isVerified ? 'none' : 'resend_email',
        severity: isVerified ? 'success' : 'warning'
      };
    }

    // If no session but we have an email, check if the user exists
    if (email) {
      // We can't directly query auth.users, but we can try to sign in to check if user exists
      // This is a non-intrusive check that won't affect the user's state
      try {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password: 'dummy_password_for_check_only'
        });

        // If we get an "Invalid login credentials" error, the user exists but password is wrong
        // If we get an "Email not confirmed" error, the user exists but isn't verified
        if (signInError?.message.includes('Email not confirmed')) {
          return {
            isVerified: false,
            canSignIn: false,
            userExists: true,
            email,
            message: "Your account exists but your email hasn't been verified yet.",
            actionRequired: 'resend_email',
            severity: 'warning'
          };
        } else if (signInError?.message.includes('Invalid login credentials')) {
          return {
            isVerified: true,
            canSignIn: true,
            userExists: true,
            email,
            message: "Your account is verified and ready to use. You can sign in with your password.",
            actionRequired: 'signin',
            severity: 'success'
          };
        }
      } catch (error) {
        console.log('[VerificationStatus] Error checking user existence:', error);
      }
    }

    return {
      isVerified: false,
      canSignIn: false,
      userExists: false,
      message: "Unable to determine your verification status. Please try signing in or contact support.",
      actionRequired: 'contact_support',
      severity: 'error'
    };
  } catch (error) {
    console.error('[VerificationStatus] Error checking verification status:', error);
    return {
      isVerified: false,
      canSignIn: false,
      userExists: false,
      message: "Error checking your verification status. Please try again or contact support.",
      actionRequired: 'contact_support',
      severity: 'error'
    };
  }
};

/**
 * Validate token and provide detailed feedback
 */
export const validateVerificationToken = async (token: string, type?: string): Promise<TokenValidationResult> => {
  try {
    // Attempt to verify the token
    const verificationResult = await supabase.auth.verifyOtp({
      token_hash: token,
      type: (type === 'recovery' || type === 'recover') ? 'recovery' : 'signup'
    });

    if (!verificationResult.error) {
      return {
        isValid: true,
        isExpired: false,
        isAlreadyUsed: false,
        tokenType: type,
        email: verificationResult.data.user?.email,
        message: "Token is valid and verification was successful!",
        suggestedAction: 'signin'
      };
    }

    const error = verificationResult.error;
    const errorMessage = error.message.toLowerCase();

    if (errorMessage.includes('expired')) {
      return {
        isValid: false,
        isExpired: true,
        isAlreadyUsed: false,
        tokenType: type,
        message: "This verification link has expired. Verification links are typically valid for 24 hours.",
        suggestedAction: 'resend'
      };
    }

    if (errorMessage.includes('invalid') || errorMessage.includes('not found')) {
      return {
        isValid: false,
        isExpired: false,
        isAlreadyUsed: true,
        tokenType: type,
        message: "This verification link is invalid or has already been used.",
        suggestedAction: 'signin'
      };
    }

    return {
      isValid: false,
      isExpired: false,
      isAlreadyUsed: false,
      tokenType: type,
      message: `Token validation failed: ${error.message}`,
      suggestedAction: 'contact_support'
    };
  } catch (error) {
    console.error('[VerificationStatus] Error validating token:', error);
    return {
      isValid: false,
      isExpired: false,
      isAlreadyUsed: false,
      tokenType: type,
      message: "Error validating your verification token. Please try again.",
      suggestedAction: 'retry'
    };
  }
};

/**
 * Resend verification email with improved error handling
 */
export const resendVerificationEmail = async (email: string): Promise<{
  success: boolean;
  message: string;
  canRetryAfter?: number;
}> => {
  try {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/verify`
      }
    });

    if (error) {
      if (error.message.includes('rate limit')) {
        return {
          success: false,
          message: "Please wait a few minutes before requesting another verification email.",
          canRetryAfter: 60 // seconds
        };
      }
      
      return {
        success: false,
        message: `Failed to resend verification email: ${error.message}`
      };
    }

    return {
      success: true,
      message: "Verification email has been resent! Please check your inbox and spam folder."
    };
  } catch (error) {
    console.error('[VerificationStatus] Error resending verification email:', error);
    return {
      success: false,
      message: "Error resending verification email. Please try again later."
    };
  }
};

/**
 * Get user-friendly verification instructions
 */
export const getVerificationInstructions = (status: VerificationStatus): {
  title: string;
  message: string;
  actions: Array<{
    label: string;
    action: string;
    variant: 'default' | 'outline' | 'destructive';
  }>;
} => {
  switch (status.actionRequired) {
    case 'none':
      return {
        title: "All Set!",
        message: status.message,
        actions: [
          { label: "Continue to App", action: "signin", variant: "default" }
        ]
      };

    case 'resend_email':
      return {
        title: "Email Verification Needed",
        message: status.message + " We can send you a new verification email.",
        actions: [
          { label: "Resend Verification Email", action: "resend", variant: "default" },
          { label: "Try Signing In", action: "signin", variant: "outline" }
        ]
      };

    case 'signin':
      return {
        title: "Ready to Sign In",
        message: status.message,
        actions: [
          { label: "Sign In Now", action: "signin", variant: "default" },
          { label: "Need Help?", action: "support", variant: "outline" }
        ]
      };

    case 'contact_support':
      return {
        title: "Need Assistance",
        message: status.message,
        actions: [
          { label: "Try Signing In", action: "signin", variant: "outline" },
          { label: "Contact Support", action: "support", variant: "default" }
        ]
      };

    default:
      return {
        title: "Verification Status Unknown",
        message: "We're having trouble determining your verification status.",
        actions: [
          { label: "Try Signing In", action: "signin", variant: "default" },
          { label: "Contact Support", action: "support", variant: "outline" }
        ]
      };
  }
};